import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import { updateAccountSettings } from 'actions'
import { getAccountSettingInfo, getAxios } from 'api'
import { toastr } from 'libs/toast'
import { Loader } from 'shared/components/Loader'
import useAxios from 'hooks/useAxios'
import { Button, Divider, Form } from 'semantic-ui-react'
import AuditLog from '../AuditLog'
import { ACTION_NAME } from '../../../hooks/useSaveUserActivityLog'
import constants from 'config/constants'
import WorkspaceTimezone from 'organisms/WorkspaceTimezone'
import WorkspaceWeekStartDay from 'components/WorkspaceWeekStartDay'
import WorkspaceBenchmarkSettings from 'components/WorkspaceBenchmarkSettings'
import store from 'store'
import ErrorText from 'shared/components/forms/ErrorText'

const COMPANY_NAME_REQUIRED_ERROR = 'Workspace name is required.'
const GENERIC_SAVE_ERROR = 'Could not save changes. Contact support for help.'
const BENCHMARK_SAVE_ERROR = 'Your settings were saved, but the benchmark industry was not.'
const BENCHMARK_SEGMENT_URL = 'account/company-segment/'

export class UpdateCompanyAccount extends React.Component {
  static propTypes = {
    updateAccountSettings: PropTypes.func,
    userInfo: PropTypes.object,
    saveUserActivityLog: PropTypes.func,
    timezoneData: PropTypes.object,
    enableWorkspaceWeekStart: PropTypes.bool,
    enableBenchmarks: PropTypes.bool,
    benchmarkSegment: PropTypes.object,
    hasManageAccountSettingsPermission: PropTypes.bool,
  }

  state = {
    isUpdateSettingDisable: false,
    loading: true,
    companyName: '',
    companyNameError: '',
    companyId: null,
    timezone: 'default',
    weekStartDay: 'monday',
    industry: '',
  }

  async componentDidMount() {
    const res = await getAccountSettingInfo()
    const data = await res.json()

    const selectedWorkspace = this.props.userInfo?.selected_workspace
    const workspaceSettings = selectedWorkspace
      ? {
          timezone: selectedWorkspace.timezone,
          weekStartDay: selectedWorkspace.week_start_day || 'monday',
        }
      : {}

    this.setState({
      token: data.token,
      loading: false,
      companyName: data.name,
      companyId: data.id,
      ...workspaceSettings,
      industry: this.props.benchmarkSegment?.industry || '',
    })
  }

  componentDidUpdate(prevProps) {
    const selectedWorkspace = this.props.userInfo?.selected_workspace
    if (!prevProps.userInfo?.selected_workspace && selectedWorkspace) {
      this.setState({
        timezone: selectedWorkspace.timezone,
        weekStartDay: selectedWorkspace.week_start_day || 'monday',
      })
    }

    const industry = this.props.benchmarkSegment?.industry
    if (industry !== prevProps.benchmarkSegment?.industry) {
      this.setState({ industry: industry || '' })
    }
  }

  saveBenchmarkIndustry = async () => {
    const industry = this.state.industry
    const hasNothingToSave =
      !this.props.enableBenchmarks ||
      !this.props.hasManageAccountSettingsPermission ||
      !industry ||
      industry === this.props.benchmarkSegment?.industry

    if (hasNothingToSave) {
      return true
    }

    try {
      await getAxios().post(BENCHMARK_SEGMENT_URL, { industry })
      this.props.benchmarkSegment?.refetch()
      return true
    } catch (error) {
      console.error('Benchmark industry save error:', error)
      return false
    }
  }

  handleSubmit = async (e) => {
    e.preventDefault()

    if (!this.props.hasManageAccountSettingsPermission) {
      return null
    }

    const trimmedName = this.state.companyName.trim()
    if (!trimmedName) {
      this.setState({ companyNameError: COMPANY_NAME_REQUIRED_ERROR })
      return
    }

    this.setState({
      isUpdateSettingDisable: true,
      companyNameError: '',
      companyName: trimmedName,
    })

    const { timezone, weekStartDay } = this.state

    const data = {
      name: trimmedName,
      ...(this.props.userInfo?.selected_workspace && {
        timezone,
        week_start_day: weekStartDay,
      }),
    }

    const companyId = this.state.companyId
    await this.props
      .updateAccountSettings(companyId, data)
      .then(async (res) => {
        const isBenchmarkIndustrySaved = await this.saveBenchmarkIndustry()
        if (isBenchmarkIndustrySaved) {
          toastr.success('Your settings were successfully updated.')
        } else {
          toastr.error(BENCHMARK_SAVE_ERROR)
        }
        const accountSetting = res?.accountSetting
        if (accountSetting) {
          store.set(constants.simplesatWorkspaceTimezone, {
            value: accountSetting.timezone,
            label: accountSetting.timezone_label,
          })
          store.set(constants.simplesatWorkspaceWeekStartDay, accountSetting.week_start_day)
        }
        this.setState({ isUpdateSettingDisable: false })

        this.props.saveUserActivityLog({
          action: ACTION_NAME.updated_company_profile,
          target: `com_${companyId}`,
          statusCode: 200,
          requestBody: data,
        })
      })
      .catch((error) => {
        console.error('Update error:', error)
        toastr.error(GENERIC_SAVE_ERROR)
        this.setState({ isUpdateSettingDisable: false })
      })
  }

  handleChangeCompanyName = (e) => {
    const value = e.target.value
    this.setState((prev) =>
      prev.companyNameError ? { companyName: value, companyNameError: '' } : { companyName: value }
    )
  }

  handleChangeTimezone = (e, { value }) => {
    this.setState({ timezone: value })
  }

  handleChangeWeekStartDay = (e, { value }) => {
    this.setState({ weekStartDay: value })
  }

  handleChangeIndustry = (e, { value }) => {
    this.setState({ industry: value })
  }

  render() {
    const loading = this.state.loading || this.props.userInfo.loading
    const shouldDisabledUpdateSettings = this.state.isUpdateSettingDisable

    if (loading) {
      return (
        <div className="row">
          <Loader withContainer />
        </div>
      )
    }

    return (
      <form className="ui form" onSubmit={this.handleSubmit}>
        <div className="grid ui stackable">
          <div className="row">
            <div className="twelve wide column">
              <h3 className="flex items-center ui header">Workspace</h3>
              <Form.Field error={Boolean(this.state.companyNameError)}>
                <label htmlFor="company-name">Workspace name</label>
                <input
                  id="company-name"
                  type="text"
                  name="company-name"
                  disabled={shouldDisabledUpdateSettings}
                  value={this.state.companyName}
                  onChange={this.handleChangeCompanyName}
                />
                {this.state.companyNameError && (
                  <ErrorText>{this.state.companyNameError}</ErrorText>
                )}
              </Form.Field>
            </div>
          </div>
          <Divider />

          <div className="row">
            <div className="twelve wide column">
              <WorkspaceTimezone
                selectedTimezone={this.state.timezone}
                timezoneOptions={this.props.timezoneData.value}
                timezoneLoading={this.props.timezoneData.loading}
                handleChangeTimezone={this.handleChangeTimezone}
              />
            </div>
          </div>
          <Divider />

          {this.props.enableWorkspaceWeekStart && (
            <React.Fragment>
              <div className="row">
                <div className="twelve wide column">
                  <WorkspaceWeekStartDay
                    selectedWeekStartDay={this.state.weekStartDay}
                    handleChangeWeekStartDay={this.handleChangeWeekStartDay}
                    disabled={shouldDisabledUpdateSettings}
                  />
                </div>
              </div>
              <Divider />
            </React.Fragment>
          )}

          {this.props.enableBenchmarks && this.props.hasManageAccountSettingsPermission && (
            <React.Fragment>
              <div className="row">
                <div className="twelve wide column">
                  <WorkspaceBenchmarkSettings
                    selectedIndustry={this.state.industry}
                    industryOptions={this.props.benchmarkSegment?.options}
                    industryLoading={this.props.benchmarkSegment?.loading}
                    handleChangeIndustry={this.handleChangeIndustry}
                    disabled={shouldDisabledUpdateSettings}
                  />
                </div>
              </div>
              <Divider />
            </React.Fragment>
          )}

          <div className="row">
            <div className="twelve wide column">
              <AuditLog userInfo={this.props.userInfo} />
            </div>
          </div>

          {this.props.hasManageAccountSettingsPermission && (
            <div className="twelve wide column">
              <Button
                type="submit"
                primary
                disabled={shouldDisabledUpdateSettings}
                onClick={this.handleSubmit}
              >
                Update settings
              </Button>
            </div>
          )}
        </div>
      </form>
    )
  }
}

const mapDispatch = (dispatch) => ({
  updateAccountSettings: (companyId, data) => dispatch(updateAccountSettings(companyId, data)),
})

const isFetching = ({ loading, data, error }) => loading || (data == null && !error)

function UpdateCompanyAccountWithData(props) {
  const userInfo = useAxios({ url: 'account/users/me/v2/' })
  const timezoneData = useAxios({ url: 'account/timezones/' })
  const isBenchmarkSegmentSkipped =
    !props.enableBenchmarks || !props.hasManageAccountSettingsPermission
  const benchmarkSegment = useAxios({ url: BENCHMARK_SEGMENT_URL, skip: isBenchmarkSegmentSkipped })

  return (
    <UpdateCompanyAccount
      {...props}
      userInfo={{ ...userInfo.data, loading: isFetching(userInfo) }}
      timezoneData={{ value: timezoneData.data ?? undefined, loading: isFetching(timezoneData) }}
      benchmarkSegment={{
        ...benchmarkSegment.data,
        loading: isBenchmarkSegmentSkipped ? false : isFetching(benchmarkSegment),
        refetch: () => {
          benchmarkSegment.fetch()
        },
      }}
    />
  )
}

export default connect(null, mapDispatch)(UpdateCompanyAccountWithData)



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/containers/Admin/UpdateCompanyAccount/index.js